import { useCallback, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import errorTracker, { type ErrorContext } from '../services/errorTracking';

/**
 * Enhanced Error Handler Hook
 *
 * Provides convenient error handling methods with automatic context injection
 * and user session data. Integrates with the global error tracking service.
 */
export const useErrorHandler = (componentName?: string) => {
  const { userInfo, idToken } = useUser();

  // Set user context when available
  useEffect(() => {
    if (userInfo?.email && userInfo?.id) {
      errorTracker.setUserContext(userInfo.id, userInfo.email);
    }
  }, [userInfo]);

  /**
   * Handle API errors with automatic context
   */
  const handleApiError = useCallback(
    (
      error: Error,
      endpoint: string,
      method: string = 'GET',
      statusCode?: number,
      responseData?: unknown,
      additionalContext?: Partial<ErrorContext>,
    ) => {
      return errorTracker.captureApiError(error, endpoint, method, statusCode, responseData, {
        component: componentName,
        userId: userInfo?.id,
        userEmail: userInfo?.email,
        ...additionalContext,
      });
    },
    [componentName, userInfo],
  );

  /**
   * Handle component errors with automatic context
   */
  const handleComponentError = useCallback(
    (error: Error, action: string, additionalContext?: Partial<ErrorContext>) => {
      return errorTracker.captureComponentError(error, componentName || 'Unknown', action, {
        userId: userInfo?.id,
        userEmail: userInfo?.email,
        ...additionalContext,
      });
    },
    [componentName, userInfo],
  );

  /**
   * Handle authentication errors
   */
  const handleAuthError = useCallback(
    (error: Error, authAction: string, additionalContext?: Partial<ErrorContext>) => {
      return errorTracker.captureAuthError(error, authAction, {
        component: componentName,
        userId: userInfo?.id,
        userEmail: userInfo?.email,
        ...additionalContext,
      });
    },
    [componentName, userInfo],
  );

  /**
   * Handle meeting-related errors
   */
  const handleMeetingError = useCallback(
    (
      error: Error,
      meetingId: string,
      meetingAction: string,
      additionalContext?: Partial<ErrorContext>,
    ) => {
      return errorTracker.captureMeetingError(error, meetingId, meetingAction, {
        component: componentName,
        userId: userInfo?.id,
        userEmail: userInfo?.email,
        roomToken: idToken || undefined,
        ...additionalContext,
      });
    },
    [componentName, userInfo, idToken],
  );

  /**
   * Handle network errors (fetch failures, timeouts, etc.)
   */
  const handleNetworkError = useCallback(
    (
      error: Error,
      url: string,
      method: string = 'GET',
      additionalContext?: Partial<ErrorContext>,
    ) => {
      return errorTracker.captureError(
        error,
        {
          component: componentName || 'Network',
          action: `network_${method.toLowerCase()}`,
          userId: userInfo?.id,
          userEmail: userInfo?.email,
          additionalData: {
            url,
            method,
            networkError: true,
            ...additionalContext?.additionalData,
          },
          ...additionalContext,
        },
        'high',
        'network',
        true,
        'major',
      );
    },
    [componentName, userInfo],
  );

  /**
   * Handle form validation errors
   */
  const handleValidationError = useCallback(
    (
      error: Error,
      formName: string,
      fieldName?: string,
      additionalContext?: Partial<ErrorContext>,
    ) => {
      return errorTracker.captureError(
        error,
        {
          component: componentName || 'Form',
          action: 'validation_error',
          userId: userInfo?.id,
          userEmail: userInfo?.email,
          additionalData: {
            formName,
            fieldName,
            validationError: true,
            ...additionalContext?.additionalData,
          },
          ...additionalContext,
        },
        'low',
        'ui',
        true,
        'minor',
      );
    },
    [componentName, userInfo],
  );

  /**
   * Handle async operation errors (promises, async/await)
   */
  const handleAsyncError = useCallback(
    (error: Error, operationName: string, additionalContext?: Partial<ErrorContext>) => {
      return errorTracker.captureError(
        error,
        {
          component: componentName || 'Async',
          action: `async_${operationName}`,
          userId: userInfo?.id,
          userEmail: userInfo?.email,
          additionalData: {
            operationName,
            asyncError: true,
            ...additionalContext?.additionalData,
          },
          ...additionalContext,
        },
        'medium',
        'unknown',
        true,
        'minor',
      );
    },
    [componentName, userInfo],
  );

  /**
   * Create error boundary handler for React components
   */
  const createErrorBoundaryHandler = useCallback(() => {
    return (error: Error, errorInfo: { componentStack?: string }) => {
      return errorTracker.captureComponentError(
        error,
        componentName || 'ErrorBoundary',
        'react_error_boundary',
        {
          userId: userInfo?.id,
          userEmail: userInfo?.email,
          additionalData: {
            componentStack: errorInfo.componentStack,
            errorBoundary: true,
            ...errorInfo,
          },
        },
      );
    };
  }, [componentName, userInfo]);

  /**
   * Wrap async functions with error handling
   */
  const withErrorHandling = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: Generic function wrapper requires flexible types
    <T extends (...args: any[]) => Promise<any>>(asyncFn: T, operationName: string): T => {
      // biome-ignore lint/suspicious/noExplicitAny: Generic function wrapper requires flexible types
      return (async (...args: any[]) => {
        try {
          return await asyncFn(...args);
        } catch (error) {
          handleAsyncError(
            error instanceof Error ? error : new Error(String(error)),
            operationName,
            {
              additionalData: {
                functionArgs: args,
                wrappedFunction: asyncFn.name || 'anonymous',
              },
            },
          );
          throw error; // Re-throw to maintain original behavior
        }
      }) as T;
    },
    [handleAsyncError],
  );

  /**
   * Get error statistics for current session
   */
  const getErrorStats = useCallback(() => {
    return errorTracker.getErrorStats();
  }, []);

  /**
   * Clear error history (useful for testing or privacy)
   */
  const clearErrors = useCallback(() => {
    errorTracker.clearErrors();
  }, []);

  return {
    // Error handling methods
    handleApiError,
    handleComponentError,
    handleAuthError,
    handleMeetingError,
    handleNetworkError,
    handleValidationError,
    handleAsyncError,

    // Utility methods
    createErrorBoundaryHandler,
    withErrorHandling,

    // Analytics methods
    getErrorStats,
    clearErrors,

    // Direct access to error tracker (for advanced usage)
    errorTracker,
  };
};

export default useErrorHandler;
